import { format, formatDistanceToNow, differenceInDays, addDays, isAfter, isBefore, isToday, parseISO } from 'date-fns';

export function formatDate(date, formatStr = 'MMM d, yyyy') {
  if (!date) return 'N/A';
  const d = typeof date === 'number' ? new Date(date) : date;
  return format(d, formatStr);
}

export function formatDateTime(date) {
  if (!date) return 'N/A';
  const d = typeof date === 'number' ? new Date(date) : date;
  return format(d, 'MMM d, yyyy h:mm a');
}

export function formatTimeAgo(date) {
  if (!date) return 'N/A';
  const d = typeof date === 'number' ? new Date(date) : date;
  return formatDistanceToNow(d, { addSuffix: true });
}

export function getDaysUntil(date) {
  if (!date) return null;
  const d = typeof date === 'number' ? new Date(date) : date;
  return differenceInDays(d, new Date());
}

export function getDaysOverdue(date) {
  const days = getDaysUntil(date);
  return days !== null && days < 0 ? Math.abs(days) : 0;
}

export function isOverdue(date) {
  if (!date) return false;
  const d = typeof date === 'number' ? new Date(date) : date;
  return isBefore(d, new Date()) && !isToday(d);
}

export function isDueSoon(date, daysThreshold = 7) {
  const days = getDaysUntil(date);
  return days !== null && days >= 0 && days <= daysThreshold;
}

export function getUpcomingBirthdays(users, daysAhead = 7) {
  const today = new Date();
  const futureDate = addDays(today, daysAhead);
  
  const getNextBirthday = (user) => {
    if (!user.profile?.dateOfBirth) return null;
    const birthday = new Date(user.profile.dateOfBirth);
    birthday.setFullYear(today.getFullYear());
    
    // If birthday already passed this year, check next year
    if (isBefore(birthday, today) && !isToday(birthday)) {
      birthday.setFullYear(today.getFullYear() + 1);
    }
    return birthday;
  };
  
  return users
    .filter(user => {
      const birthday = getNextBirthday(user);
      if (!birthday) return false;
      return !isAfter(birthday, futureDate);
    })
    .sort((a, b) => {
      const dateA = getNextBirthday(a);
      const dateB = getNextBirthday(b);
      return dateA - dateB;
    });
}

export function getUpcomingAnniversaries(users, daysAhead = 30) {
  const today = new Date();
  const futureDate = addDays(today, daysAhead);
  
  const getNextAnniversary = (user) => {
    if (!user.profile?.hireDate) return null;
    const anniversary = new Date(user.profile.hireDate);
    anniversary.setFullYear(today.getFullYear());
    
    if (isBefore(anniversary, today) && !isToday(anniversary)) {
      anniversary.setFullYear(today.getFullYear() + 1);
    }
    return anniversary;
  };
  
  return users
    .filter(user => {
      const anniversary = getNextAnniversary(user);
      if (!anniversary) return false;
      return !isAfter(anniversary, futureDate);
    })
    .sort((a, b) => {
      const dateA = getNextAnniversary(a);
      const dateB = getNextAnniversary(b);
      return dateA - dateB;
    });
}

export function getYearsOfService(hireDate) {
  if (!hireDate) return 0;
  const d = typeof hireDate === 'number' ? new Date(hireDate) : hireDate;
  const years = differenceInDays(new Date(), d) / 365;
  return Math.floor(years);
}

// Get current year
export function getCurrentYear() {
  return new Date().getFullYear();
}

// Get current month (1-12)
export function getCurrentMonth() {
  return new Date().getMonth() + 1;
}

// Get month name from month number (1-12)
export function getMonthName(month) {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  return months[(month - 1) % 12] || 'Unknown';
}

// Get days between two dates
export function getDaysBetween(date1, date2) {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const diffTime = Math.abs(d2 - d1);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}
